// packages/ui/src/primitives/flags/FlagWithLabelCore.tsx
//
// A flag with its label, without MUI or emotion: the one layout of every flag-plus-label in TourTask. The flag is
// 1.15em square, 8 px before the label, both centred on one line (in a right-to-left page the flag stands on the
// right, as flex lays it out). `@tourtask/ui/components/FlagWithLabel` is this with MUI's body2 label; a hosted
// website's language switcher (apps/sites) uses it as it is. The flag is decorative, since the label next to it
// names it: `alt=""`, so a screen reader reads the label once and no file name.
import type { CSSProperties, ReactNode } from 'react';

import { FlagCore, type FlagImageOptions, type FlagSubject } from './FlagCore';

// The label alone takes part in baseline alignment, so the row's baseline is the label's: on a line of text (a
// checkbox's label, a table cell) the label sits on that line's baseline like plain text. With the flag first and
// every item centred, the row's baseline was the flag's bottom edge, which lifted flag and label about 3 px above
// the checkbox and the text beside them (the owner, 2026-10-25). The label stands at the row's top and the flag in
// its middle: both centred while the label's line is at least as tall as the flag (a line-height of 1.15 or more;
// MUI's body2 is 1.43, and a hosted website's language button sets 1.15 for this).
const FLAG_WITH_LABEL_STYLE: CSSProperties = { display: 'inline-flex', alignItems: 'baseline', gap: 8 };
// Never narrower than square: where the line is short of room, the label gives way, not the flag.
const FLAG_BESIDE_LABEL_STYLE: CSSProperties = { width: '1.15em', height: '1.15em', flexShrink: 0, alignSelf: 'center' };

export type FlagWithLabelCoreProps = FlagSubject &
  FlagImageOptions & {
    /** What names the flag: plain text, or the caller's own element (MUI's Typography, a `<span lang>`). */
    label: ReactNode;
    /**
     * The element around flag and label. `span` (the default) is phrasing content, so it fits in a `<summary>`, an
     * `<a>` or a `<button>`; `div` holds a block label such as MUI's Typography, a `<p>`.
     */
    as?: 'span' | 'div';
  };

export function FlagWithLabelCore({ label, as: Element = 'span', source, loading, languageCode, countryCode }: FlagWithLabelCoreProps) {
  const image = { style: FLAG_BESIDE_LABEL_STYLE, alt: '', source, loading };
  return (
    <Element style={FLAG_WITH_LABEL_STYLE}>
      {languageCode !== undefined ? <FlagCore languageCode={languageCode} {...image} /> : <FlagCore countryCode={countryCode} {...image} />}
      {label}
    </Element>
  );
}
